export interface SpecFieldDef {
  key: string;
  label: string;
  unit?: string;
  type: "number" | "text" | "select";
  options?: string[];
}

// Logistics/manufacturing fields that can apply to any product regardless of
// subcategory (weight, dimensions, warranty, country) — kept separate from
// SUBCATEGORY_SPEC_FIELDS below since those are specific to one equipment
// type. Used only for detail-page label lookup, not catalog filtering.
export const COMMON_SPEC_FIELDS: SpecFieldDef[] = [
  { key: "sku", label: "Код товара", type: "text" },
  { key: "country", label: "Страна производства", type: "text" },
  { key: "weight_kg", label: "Вес нетто", unit: "кг", type: "number" },
  { key: "weight_kg_gross", label: "Вес брутто", unit: "кг", type: "number" },
  { key: "dimensions_mm", label: "Габариты (ШxГxВ)", unit: "мм", type: "text" },
  { key: "length_mm", label: "Длина", unit: "мм", type: "number" },
  { key: "depth_mm", label: "Глубина", unit: "мм", type: "number" },
  { key: "height_mm", label: "Высота", unit: "мм", type: "number" },
  { key: "voltage", label: "Напряжение", type: "text" },
  { key: "volume_m3", label: "Объём", unit: "м³", type: "number" },
  { key: "warranty_months", label: "Гарантия", unit: "мес.", type: "number" },
  { key: "meals_per_day", label: "Порций в день", type: "text" },
  { key: "power_gas_kw", label: "Мощность (газ)", unit: "кВт", type: "number" },
  { key: "weight_kg_gas_version", label: "Вес (газовая версия)", unit: "кг", type: "number" },
  {
    key: "heating_type",
    label: "Тип нагрева",
    type: "select",
    options: ["Электрическое", "Газовое", "Паровое", "Дровяное"],
  },
];

// Life Concept's glaze-finish names, as labeled in their own catalog next
// to each colorway swatch (ELM/Cream/JUDE etc. are the manufacturer's own
// internal codes — this project's own Russian gloss on them, since the
// catalog itself never spells out a full color name). These codes are NOT
// a fixed universal palette — confirmed the hard way: "M2" turned out to be
// a warm dark-bronze speckle on one product line (Isamu) and a pale
// blue-grey on others (Auberge, Pebble) once real product photos replaced
// the tiny catalog thumbnails. Verify against an actual photo before
// reusing a code→color mapping across different product lines.
const TABLEWARE_GLAZE_COLORS = [
  "Экрю",
  "Кремовый",
  "Нефритовый",
  "Графит",
  "Дымчато-голубой",
  "Бронза",
  "Антрацит",
  "Шалфейный",
  "Туманно-голубой",
  "Лагуна",
  "Чёрный",
  "Небесный",
];

/**
 * Which characteristic fields are relevant for each subcategory — drives
 * both the admin product form's dynamic fields and the catalog sidebar's
 * dynamic filters. A subcategory not listed here just gets no dynamic
 * fields (brand/subcategory selection still works).
 */
export const SUBCATEGORY_SPEC_FIELDS: Record<string, SpecFieldDef[]> = {
  // Тепловое
  "Пароконвектоматы": [
    { key: "levels", label: "Количество уровней", type: "number" },
    { key: "power_kw", label: "Мощность", unit: "кВт", type: "number" },
    { key: "control_type", label: "Тип управления", type: "select", options: ["Механическое", "Электронное", "Сенсорное"] },
    { key: "energy_type", label: "Тип", type: "select", options: ["Электрический", "Газовый"] },
  ],
  "Печи для пиццы": [
    { key: "chambers", label: "Количество камер", type: "number" },
    { key: "chamber_mm", label: "Размер камеры", unit: "мм", type: "text" },
    { key: "power_kw", label: "Мощность", unit: "кВт", type: "number" },
    { key: "max_pizza_diameter_cm", label: "Макс. диаметр пиццы", unit: "см", type: "number" },
    {
      key: "control_type",
      label: "Тип управления",
      type: "select",
      options: ["Электромеханическое", "Цифровое", "Сенсорное"],
    },
    {
      key: "mount_type",
      label: "Способ монтажа",
      type: "select",
      options: ["Подвесная", "Отдельностоящая"],
    },
  ],
  "Сковороды опрокидываемые": [
    { key: "power_kw", label: "Мощность", unit: "кВт", type: "number" },
    { key: "power_kw_balanced_power_option", label: "Мощность (Balanced Power)", unit: "кВт", type: "number" },
    { key: "energy_type", label: "Тип", type: "select", options: ["Электрический", "Газовый"] },
  ],
  "Высокоскоростные печи": [
    { key: "control_type", label: "Тип управления", type: "select", options: ["Сенсорное", "Цифровое"] },
  ],
  "Аксессуары": [{ key: "compatible_with", label: "Совместимость", type: "text" }],
  "Грили": [
    { key: "power_kw", label: "Мощность", unit: "кВт", type: "number" },
    { key: "temp_max_c", label: "Макс. температура", unit: "°C", type: "number" },
    { key: "grid_material", label: "Материал решётки", type: "select", options: ["Чугун", "Сталь"] },
    { key: "energy_type", label: "Тип", type: "select", options: ["Электрический", "Газовый"] },
  ],
  "Плиты кухонные": [
    { key: "burners", label: "Количество конфорок", type: "number" },
    { key: "power_kw", label: "Мощность", unit: "кВт", type: "number" },
    { key: "energy_type", label: "Тип питания", type: "select", options: ["Электрический", "Газовый", "Индукционный"] },
  ],
  "Плиты контактной жарки": [
    { key: "energy_type", label: "Тип питания", type: "select", options: ["Электрический", "Газовый"] },
  ],
  "Варочные котлы": [
    { key: "volume_liters", label: "Объём", unit: "л", type: "number" },
    { key: "energy_type", label: "Тип питания", type: "select", options: ["Электрический", "Газовый"] },
  ],
  "Фритюрницы": [
    { key: "energy_type", label: "Тип питания", type: "select", options: ["Электрический", "Газовый"] },
    { key: "power_kw", label: "Мощность", unit: "кВт", type: "number" },
  ],
  "Пароварочные аппараты": [
    { key: "capacity_gn", label: "Ёмкость", unit: "GN 1/1", type: "number" },
    { key: "pressure_bar", label: "Давление", unit: "бар", type: "number" },
  ],
  "Пастоварки": [
    { key: "capacity_kg", label: "Загрузка", unit: "кг", type: "number" },
    { key: "energy_type", label: "Тип питания", type: "select", options: ["Электрический", "Газовый", "Паровой"] },
  ],
  "Аппараты для кур-гриль": [
    { key: "skewers", label: "Количество шампуров", type: "number" },
    { key: "swing_baskets", label: "Количество балансиров", type: "number" },
    { key: "capacity_units", label: "Вместимость", type: "text" },
    { key: "color", label: "Цвет", type: "text" },
    { key: "energy_type", label: "Тип питания", type: "select", options: ["Электрический", "Газовый"] },
  ],

  // Холодильное
  "Шкафы шоковой заморозки": [
    { key: "volume_liters", label: "Объём", unit: "л", type: "number" },
    { key: "capacity_gn", label: "Ёмкость", unit: "GN 1/1", type: "number" },
    { key: "temp_range", label: "Диапазон температур", type: "text" },
    { key: "output_cooled_kg", label: "Выход охлаждённого продукта", unit: "кг", type: "number" },
    { key: "output_frozen_kg", label: "Выход замороженного продукта", unit: "кг", type: "number" },
    { key: "tray_pitch_mm", label: "Расстояние между уровнями", unit: "мм", type: "number" },
    { key: "capacity_kg", label: "Загрузка за цикл", unit: "кг", type: "number" },
  ],
  "Столы охлаждаемые": [
    { key: "volume_liters", label: "Объём", unit: "л", type: "number" },
    { key: "doors", label: "Количество дверей", type: "number" },
    { key: "refrigerant", label: "Хладагент", type: "select", options: ["R290", "R134a", "R404A"] },
  ],
  "Льдогенераторы": [
    { key: "output_kg_day", label: "Производительность", unit: "кг/сутки", type: "number" },
    { key: "ice_type", label: "Тип льда", type: "select", options: ["Кубиковый", "Чешуйчатый", "Гранулированный"] },
  ],
  "Холодильные шкафы": [
    { key: "volume_liters", label: "Объём", unit: "л", type: "number" },
    { key: "temp_range", label: "Диапазон температур", type: "text" },
    { key: "refrigerant", label: "Хладагент", type: "select", options: ["R290", "R134a", "R404A"] },
  ],

  // Посудомоечное
  "Фронтальные": [
    { key: "cycles_per_hour", label: "Циклов в час", type: "number" },
    { key: "basket_size_cm", label: "Размер корзины", unit: "см", type: "text" },
  ],
  "Купольные": [
    { key: "cycles_per_hour", label: "Циклов в час", type: "number" },
    { key: "power_kw", label: "Мощность", unit: "кВт", type: "number" },
  ],
  "Туннельные машины": [
    { key: "capacity_plates_hour", label: "Производительность", unit: "тарелок/час", type: "number" },
    { key: "power_kw", label: "Мощность", unit: "кВт", type: "number" },
  ],

  // Пекарское
  "Конвекционные печи": [
    { key: "trays", label: "Количество противней", type: "number" },
    { key: "control_type", label: "Тип управления", type: "select", options: ["Сенсорное", "Цифровое"] },
  ],
  "Подовые печи": [
    { key: "decks", label: "Количество камер", type: "number" },
    { key: "baking_size_cm", label: "Размер пода", type: "text" },
    { key: "control_type", label: "Тип управления", type: "select", options: ["Сенсорное", "Цифровое"] },
  ],
  "Витрины тепловые": [{ key: "control_type", label: "Тип управления", type: "text" }],
  "Тестомесы": [
    { key: "bowl_volume_liters", label: "Объём дежи", unit: "л", type: "number" },
    { key: "dough_capacity_kg", label: "Замес муки", unit: "кг", type: "number" },
    { key: "dough_kg", label: "Замес муки", unit: "кг", type: "number" },
    { key: "capacity_kg", label: "Замес муки", unit: "кг", type: "number" },
  ],
  "Расстоечные шкафы": [
    { key: "levels", label: "Количество уровней", type: "number" },
    { key: "capacity_trays", label: "Ёмкость", unit: "листов", type: "number" },
    { key: "capacity_trolleys", label: "Ёмкость по тележкам", type: "text" },
    { key: "capacity_pcs", label: "Вместимость", unit: "заготовок", type: "number" },
    { key: "temp_range", label: "Диапазон температур", type: "text" },
  ],
  "Охладители воды": [
    { key: "tank_volume_liters", label: "Объём бака", unit: "л", type: "number" },
    { key: "hourly_output_liters", label: "Производительность", unit: "л/час", type: "number" },
    { key: "capacity_lh", label: "Производительность", unit: "л/ч", type: "number" },
    { key: "flow_lmin", label: "Расход воды", unit: "л/мин", type: "number" },
  ],
  "Тестораскаточные машины": [
    { key: "roller_width_mm", label: "Ширина валков", unit: "мм", type: "number" },
    { key: "power_kw", label: "Мощность", unit: "кВт", type: "number" },
    { key: "max_length_cm", label: "Макс. длина заготовки", unit: "см", type: "number" },
  ],
  "Мукопросеиватели": [
    { key: "discharge_height_cm", label: "Высота выгрузки", unit: "см", type: "number" },
  ],
  "Хлеборезки": [
    { key: "max_piece_height_mm", label: "Макс. высота заготовки", unit: "мм", type: "number" },
    { key: "width_mm", label: "Макс. ширина заготовки", unit: "мм", type: "number" },
  ],
  "Овощерезательные и протирочные машины": [
    { key: "capacity_kg_hour", label: "Производительность", unit: "кг/ч", type: "number" },
  ],

  // Кофейное & Барное
  "Кофемашины": [
    { key: "groups", label: "Количество групп", type: "number" },
    { key: "boiler_liters", label: "Объём бойлера", unit: "л", type: "number" },
    { key: "power_kw", label: "Мощность", unit: "кВт", type: "number" },
    { key: "power_w", label: "Мощность", unit: "Вт", type: "number" },
    { key: "hopper_g", label: "Объём бункера для зёрен", unit: "г", type: "number" },
    { key: "type", label: "Тип", type: "select", options: ["Автоматическая", "Рожковая", "Капсульная"] },
    {
      key: "steam_control",
      label: "Управление паром",
      type: "select",
      options: ["Односторонний", "Двусторонний"],
    },
    { key: "version", label: "Версия", type: "select", options: ["Single", "IoT"] },
  ],
  "Блендеры": [
    { key: "power_w", label: "Мощность", unit: "Вт", type: "number" },
    { key: "jug_volume_liters", label: "Объём чаши", unit: "л", type: "number" },
  ],
  "Соковыжималки": [
    { key: "power_w", label: "Мощность", unit: "Вт", type: "number" },
    { key: "type", label: "Тип", type: "select", options: ["Шнековая", "Центробежная", "Цитрусовая"] },
  ],
  "Стимеры для молока": [
    { key: "power_w", label: "Мощность", unit: "Вт", type: "number" },
    { key: "boiler_liters", label: "Объём бойлера", unit: "л", type: "number" },
    { key: "tank_liters", label: "Объём бака", unit: "л", type: "number" },
    { key: "milk_tank_liters", label: "Объём бака для молока", unit: "л", type: "number" },
    { key: "cooling_temp_c", label: "Температура охлаждения", unit: "°C", type: "text" },
  ],
  "Диспенсеры горячей воды": [
    { key: "power_w", label: "Мощность", unit: "Вт", type: "number" },
    { key: "boiler_liters", label: "Объём бойлера", unit: "л", type: "number" },
  ],
  "Автоматы для чайных напитков": [
    { key: "power_w", label: "Мощность", unit: "Вт", type: "number" },
    { key: "ingredient_slots", label: "Количество слотов для ингредиентов", type: "number" },
  ],
  "Миксеры для молочных коктейлей": [
    { key: "power_w", label: "Мощность", unit: "Вт", type: "number" },
    { key: "cup_capacity_ml", label: "Объём стакана", unit: "мл", type: "number" },
    { key: "speed_rpm_max", label: "Макс. скорость вращения", unit: "об/мин", type: "number" },
  ],
  "Аксессуары для бара": [
    { key: "power_w", label: "Мощность", unit: "Вт", type: "number" },
    { key: "hopper_kg", label: "Объём бункера", unit: "кг", type: "number" },
    { key: "tamper_mm", label: "Диаметр темпера", unit: "мм", type: "number" },
    { key: "rotation_rpm", label: "Скорость вращения", unit: "об/мин", type: "number" },
    { key: "capacity_liters", label: "Объём", unit: "л", type: "number" },
  ],
  "Закаточные машины для банок": [
    { key: "voltage_v", label: "Напряжение блока питания", unit: "В", type: "text" },
    { key: "seaming_time_sec", label: "Время закатки банки", unit: "сек", type: "number" },
    { key: "color", label: "Цвет корпуса", type: "text" },
    { key: "volume_ml", label: "Объём банки", unit: "мл", type: "number" },
    {
      key: "control_type",
      label: "Тип управления",
      type: "select",
      options: ["Механическое", "Цифровое", "Электронное"],
    },
  ],

  // Индустриальное
  "Мясорубки": [
    { key: "power_kw", label: "Мощность", unit: "кВт", type: "number" },
    { key: "capacity_kg_hour", label: "Производительность", unit: "кг/час", type: "number" },
  ],
  "Овощерезки": [
    { key: "power_kw", label: "Мощность", unit: "кВт", type: "number" },
    { key: "capacity_kg_hour", label: "Производительность", unit: "кг/час", type: "number" },
  ],
  "Пилы ленточные": [
    { key: "power_kw", label: "Мощность", unit: "кВт", type: "number" },
    { key: "blade_length_mm", label: "Длина полотна", unit: "мм", type: "number" },
  ],
  "Слайсеры": [
    { key: "blade_diameter_mm", label: "Диаметр ножа", unit: "мм", type: "number" },
    { key: "power_w", label: "Мощность", unit: "Вт", type: "number" },
  ],
  "Овощемойки": [
    { key: "capacity_kg_hour", label: "Производительность", unit: "кг/час", type: "number" },
    { key: "capacity_kg_cycle", label: "Загрузка на цикл", unit: "кг", type: "number" },
  ],
  "Сушилки для овощей": [
    { key: "capacity_liters", label: "Ёмкость барабана", unit: "л", type: "number" },
    { key: "capacity_kg_hour", label: "Производительность", unit: "кг/час", type: "number" },
  ],
  "Овощеочистительные машины": [
    { key: "capacity_kg", label: "Загрузка", unit: "кг", type: "number" },
    { key: "cycle_minutes", label: "Цикл очистки", unit: "мин", type: "number" },
  ],
  "Куттеры": [
    { key: "capacity_liters", label: "Ёмкость чаши", unit: "л", type: "number" },
    { key: "speed_rpm", label: "Скорость", unit: "об/мин", type: "text" },
  ],
  "Формовочные машины": [
    { key: "output_pieces_hour", label: "Производительность", unit: "шт/час", type: "number" },
  ],
  "Панировочные машины": [
    { key: "output_pieces_hour", label: "Производительность", unit: "шт/час", type: "number" },
  ],
  "Дозирующее оборудование": [
    { key: "power_kw", label: "Мощность", unit: "кВт", type: "number" },
    { key: "dosing_range_ml", label: "Диапазон дозирования", unit: "мл", type: "text" },
    { key: "output_pieces_hour", label: "Производительность", unit: "шт/час", type: "number" },
  ],
  "Автоматизация и обработка тары": [
    { key: "container_format_g", label: "Формат тары", unit: "г", type: "text" },
    { key: "output_pieces_hour", label: "Производительность", unit: "шт/час", type: "number" },
  ],

  // Нейтральное
  "Столы производственные": [
    { key: "width_cm", label: "Ширина", unit: "см", type: "number" },
    { key: "material", label: "Материал", type: "select", options: ["Нержавеющая сталь", "Оцинкованная сталь"] },
  ],
  "Ванны моечные": [
    { key: "sections", label: "Количество секций", type: "number" },
    { key: "width_cm", label: "Ширина", unit: "см", type: "number" },
  ],
  "Стеллажи": [
    { key: "shelves", label: "Количество полок", type: "number" },
    { key: "load_kg", label: "Нагрузка на полку", unit: "кг", type: "number" },
  ],
  "Тележки": [
    { key: "capacity", label: "Вместимость", type: "text" },
  ],

  // Линии раздачи
  "Мармиты": [
    { key: "power_kw", label: "Мощность", unit: "кВт", type: "number" },
    { key: "power_w", label: "Мощность", unit: "Вт", type: "number" },
    { key: "voltage", label: "Напряжение", type: "text" },
    { key: "gn_capacity", label: "Ёмкость", unit: "GN 1/1", type: "number" },
    { key: "energy_type", label: "Тип", type: "select", options: ["Электрический", "Газовый"] },
    { key: "capacity_plates", label: "Вместимость", unit: "тарелок", type: "number" },
  ],
  "Тепловые ленты": [
    { key: "power_w", label: "Мощность", unit: "Вт", type: "number" },
    { key: "voltage", label: "Напряжение", type: "text" },
    { key: "length_mm", label: "Длина", unit: "мм", type: "number" },
  ],
  "Тепловые лампы": [
    { key: "power_w", label: "Мощность", unit: "Вт", type: "number" },
    { key: "voltage", label: "Напряжение", type: "text" },
    { key: "lamp_count", label: "Количество ламп", type: "number" },
  ],
  "Фрай-станции": [
    { key: "power_w", label: "Мощность", unit: "Вт", type: "number" },
    { key: "voltage", label: "Напряжение", type: "text" },
    { key: "gn_capacity", label: "Ёмкость", unit: "GN 1/1", type: "number" },
  ],
  "Карвинг-станции": [
    { key: "power_w", label: "Мощность", unit: "Вт", type: "number" },
    { key: "voltage", label: "Напряжение", type: "text" },
  ],
  "Портативные мармиты": [
    { key: "power_w", label: "Мощность", unit: "Вт", type: "number" },
    { key: "voltage", label: "Напряжение", type: "text" },
    { key: "gn_capacity", label: "Ёмкость", unit: "GN 1/1", type: "number" },
  ],
  "Тепловые ящики": [
    { key: "power_w", label: "Мощность", unit: "Вт", type: "number" },
    { key: "voltage", label: "Напряжение", type: "text" },
    { key: "drawer_count", label: "Количество ящиков", type: "number" },
  ],
  "Встраиваемые мармиты": [
    { key: "power_w", label: "Мощность", unit: "Вт", type: "number" },
    { key: "voltage", label: "Напряжение", type: "text" },
    { key: "gn_capacity", label: "Ёмкость", unit: "GN 1/1", type: "number" },
  ],
  "Витрины-мармиты": [
    { key: "power_w", label: "Мощность", unit: "Вт", type: "number" },
    { key: "voltage", label: "Напряжение", type: "text" },
    { key: "shelf_count", label: "Количество полок", type: "number" },
  ],
  "Тепловые шкафы": [
    { key: "power_w", label: "Мощность", unit: "Вт", type: "number" },
    { key: "voltage", label: "Напряжение", type: "text" },
    { key: "capacity", label: "Вместимость", type: "text" },
  ],
  "Тостеры": [
    { key: "power_w", label: "Мощность", unit: "Вт", type: "number" },
    { key: "voltage", label: "Напряжение", type: "text" },
    { key: "slots", label: "Количество слотов", type: "number" },
  ],
  "Индукционное оборудование": [
    { key: "power_w", label: "Мощность", unit: "Вт", type: "number" },
    { key: "voltage", label: "Напряжение", type: "text" },
    { key: "color", label: "Цвет", type: "text" },
  ],
  "Легкое кухонное оборудование": [
    { key: "power_w", label: "Мощность", unit: "Вт", type: "number" },
    { key: "voltage", label: "Напряжение", type: "text" },
  ],
  "Водонагреватели": [
    { key: "power_w", label: "Мощность", unit: "Вт", type: "number" },
    { key: "voltage", label: "Напряжение", type: "text" },
    { key: "tank_liters", label: "Объём бака", unit: "л", type: "number" },
  ],
  "Прилавки охлаждаемые": [
    { key: "gn_capacity", label: "Ёмкость", unit: "GN 1/1", type: "number" },
    { key: "temp_range", label: "Диапазон температур", type: "text" },
    { key: "tier", label: "Комплектация", type: "text" },
  ],
  "Прилавки тепловые": [
    { key: "gn_capacity", label: "Ёмкость", unit: "GN 1/1", type: "number" },
    { key: "power_kw", label: "Мощность", unit: "кВт", type: "number" },
    { key: "tier", label: "Комплектация", type: "text" },
  ],
  "Лампы для линий раздачи": [
    { key: "gn_capacity", label: "Ёмкость", unit: "GN 1/1", type: "number" },
    { key: "power_w", label: "Мощность", unit: "Вт", type: "number" },
    { key: "color", label: "Цвет / отделка", type: "select", options: TABLEWARE_GLAZE_COLORS },
    { key: "size_cm", label: "Размер", unit: "см", type: "number" },
    { key: "material", label: "Материал", type: "text" },
  ],
  "Тележки сервировочные": [
    { key: "capacity", label: "Вместимость", type: "text" },
  ],
  "Модульные линии на заказ": [
    { key: "depth", label: "Глубина модуля", type: "select", options: ["Small", "Medium", "Large"] },
    { key: "gn_range", label: "Длина модуля", type: "text" },
    { key: "height", label: "Высота модуля", type: "text" },
    { key: "tier", label: "Комплектация", type: "text" },
  ],
  "Буфетные линии": [
    { key: "thermostat", label: "Термостат цифровой", type: "text" },
    { key: "trays", label: "Гастроёмкости в комплекте", type: "text" },
    { key: "tier", label: "Комплектация", type: "text" },
  ],
  "Тестомесы спиральные": [
    { key: "capacity_kg", label: "Замес теста", unit: "кг", type: "number" },
  ],
  "Тестоокруглители": [
    { key: "weight_range", label: "Диапазон заготовок", type: "text" },
    { key: "belt_length_mm", label: "Длина ленты", unit: "мм", type: "number" },
    { key: "piece_weight_max_g", label: "Макс. вес заготовки", unit: "г", type: "number" },
  ],
  "Тестоделители": [
    { key: "divisions", label: "Количество делений", type: "number" },
  ],
  "Картофелечистки": [
    { key: "capacity_kg", label: "Загрузка", unit: "кг", type: "number" },
    { key: "capacity_l", label: "Объём барабана", unit: "л", type: "number" },
    { key: "cycle_minutes", label: "Цикл очистки", unit: "мин", type: "number" },
    { key: "capacity_kg_hour", label: "Производительность", unit: "кг/час", type: "number" },
  ],

  // Прачечное
  "Стиральные машины": [
    { key: "capacity_kg", label: "Загрузка", unit: "кг", type: "number" },
    { key: "spin_rpm", label: "Отжим", unit: "об/мин", type: "number" },
  ],
  "Сушильные автоматы": [
    { key: "capacity_kg", label: "Загрузка", unit: "кг", type: "number" },
    { key: "energy_type", label: "Тип питания", type: "select", options: ["Электрический", "Газовый"] },
  ],

  // Посуда и инвентарь — glaze/finish is the only thing that varies between
  // same-shape variants for Life Concept; Tognana's size_cm covers its own
  // common case, same-shape SKUs that only differ by size (and often share
  // one catalogue photo across every size — see tognanaSeed.ts) — without
  // it the config picker had nothing but the raw SKU code to tell same-
  // photo size variants apart.
  "Тарелки": [
    { key: "color", label: "Цвет глазури", type: "select", options: TABLEWARE_GLAZE_COLORS },
    { key: "size_cm", label: "Размер", unit: "см", type: "number" },
    { key: "material", label: "Материал", type: "text" },
    { key: "collection", label: "Коллекция", type: "text" },
    { key: "height_cm", label: "Высота", unit: "см", type: "number" },
    { key: "finish", label: "Отделка", type: "text" },
  ],
  "Пиалы и чаши": [
    { key: "color", label: "Цвет глазури", type: "select", options: TABLEWARE_GLAZE_COLORS },
    { key: "size_cm", label: "Размер", unit: "см", type: "number" },
    { key: "material", label: "Материал", type: "text" },
    { key: "collection", label: "Коллекция", type: "text" },
    { key: "height_cm", label: "Высота", unit: "см", type: "number" },
    { key: "finish", label: "Отделка", type: "text" },
  ],
  "Супницы": [
    { key: "color", label: "Цвет глазури", type: "select", options: TABLEWARE_GLAZE_COLORS },
    { key: "size_cm", label: "Размер", unit: "см", type: "number" },
    { key: "material", label: "Материал", type: "text" },
    { key: "collection", label: "Коллекция", type: "text" },
  ],
  "Соусники и блюдца": [
    { key: "color", label: "Цвет глазури", type: "select", options: TABLEWARE_GLAZE_COLORS },
    { key: "size_cm", label: "Размер", unit: "см", type: "number" },
    { key: "material", label: "Материал", type: "text" },
    { key: "collection", label: "Коллекция", type: "text" },
    { key: "height_cm", label: "Высота", unit: "см", type: "number" },
    { key: "finish", label: "Отделка", type: "text" },
  ],
  "Подносы и лотки": [
    { key: "color", label: "Цвет глазури", type: "select", options: TABLEWARE_GLAZE_COLORS },
    { key: "size_cm", label: "Размер", unit: "см", type: "number" },
    { key: "material", label: "Материал", type: "text" },
    { key: "collection", label: "Коллекция", type: "text" },
    { key: "height_cm", label: "Высота", unit: "см", type: "number" },
    { key: "finish", label: "Отделка", type: "text" },
  ],
  "Чашки и сервировочные наборы": [
    { key: "color", label: "Цвет глазури", type: "select", options: TABLEWARE_GLAZE_COLORS },
    { key: "size_cm", label: "Размер", unit: "см", type: "number" },
    { key: "material", label: "Материал", type: "text" },
    { key: "collection", label: "Коллекция", type: "text" },
  ],
  // Chef's/kitchen/table knives and knife sets — introduced by Arcos's
  // catalogue (see arcosSeed.ts), the first brand in this codebase whose
  // core product IS a knife rather than flatware/tableware alongside one.
  "Ножи": [
    { key: "length_cm", label: "Длина лезвия", unit: "см", type: "number" },
    { key: "material", label: "Материал", type: "text" },
    { key: "collection", label: "Коллекция", type: "text" },
  ],
  "Столовые приборы": [
    { key: "color", label: "Цвет / отделка", type: "select", options: TABLEWARE_GLAZE_COLORS },
    { key: "size_cm", label: "Размер", unit: "см", type: "number" },
    { key: "material", label: "Материал", type: "text" },
    { key: "collection", label: "Коллекция", type: "text" },
    { key: "height_cm", label: "Высота", unit: "см", type: "number" },
    { key: "finish", label: "Отделка", type: "text" },
  ],
  "Стеклянная посуда": [
    { key: "color", label: "Цвет / отделка", type: "select", options: TABLEWARE_GLAZE_COLORS },
    { key: "size_cm", label: "Размер", unit: "см", type: "number" },
    { key: "material", label: "Материал", type: "text" },
    { key: "collection", label: "Коллекция", type: "text" },
  ],
  "Посуда для готовки": [
    { key: "color", label: "Цвет / отделка", type: "select", options: TABLEWARE_GLAZE_COLORS },
    { key: "size_cm", label: "Размер", unit: "см", type: "number" },
    { key: "material", label: "Материал", type: "text" },
    { key: "collection", label: "Коллекция", type: "text" },
  ],
  "Кухонные принадлежности": [
    { key: "color", label: "Цвет / отделка", type: "select", options: TABLEWARE_GLAZE_COLORS },
    { key: "size_cm", label: "Размер", unit: "см", type: "number" },
    { key: "material", label: "Материал", type: "text" },
    { key: "collection", label: "Коллекция", type: "text" },
  ],
  // Display risers/stands/holders (podiums, cubes, cylinders, waffle/tacos
  // holders) — a shape family Tognana/Life Concept didn't carry, introduced
  // by SuMisura's catalogue (see sumisuraSeed.ts).
  "Подставки и подиумы": [
    { key: "color", label: "Цвет / отделка", type: "select", options: TABLEWARE_GLAZE_COLORS },
    { key: "size_cm", label: "Размер", unit: "см", type: "number" },
    { key: "material", label: "Материал", type: "text" },
    { key: "collection", label: "Коллекция", type: "text" },
    { key: "height_cm", label: "Высота", unit: "см", type: "number" },
    { key: "finish", label: "Отделка", type: "text" },
  ],
};
